import ObjectHash from 'object-hash'

export const state = () => {
	return {
		statsGlobal: {},
		ttl_statsGlobal: {},
		cache_key_statsGlobal: {},

		statsImpact: [],
		ttl_statsImpact: {},
		cache_key_statsImpact: {},

		statsLeaderboard: { results: [], next: null, count: 0 },
		ttl_statsLeaderboard: {},
		cache_key_statsLeaderboard: {},

		statsSocioDemo: {},
		ttl_statsSocioDemo: {},
		cache_key_statsSocioDemo: {},

		cache_duration: 10800000, // 3 hours
	}
}

function isInCacheStatsGlobal(state, statKey, companyId, params) {
	if (!state.ttl_statsGlobal[statKey] || state.ttl_statsGlobal[statKey] < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsGlobal(statKey, companyId, params)
	return state.cache_key_statsGlobal[statKey] === cacheKey
}

function isInCacheStatsImpact(state, companyId, params) {
	if (!state.ttl_statsImpact || state.ttl_statsImpact < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsImpact(companyId, params)
	return state.cache_key_statsImpact === cacheKey
}

function isInCacheStatsLeaderboard(state, companyId, params) {
	if (!state.ttl_statsLeaderboard || state.ttl_statsLeaderboard < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsLeaderboard(companyId, params)
	return state.cache_key_statsLeaderboard === cacheKey
}

function isInCacheStatsSocioDemo(state, statKey, companyId) {
	if (!state.ttl_statsSocioDemo[statKey] || state.ttl_statsSocioDemo[statKey] < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsSocioDemo(statKey, companyId)
	return state.cache_key_statsSocioDemo[statKey] === cacheKey
}

function buildCacheKeyStatsGlobal(statKey, companyId, params) {
	return `company:${companyId}:global::params:${ObjectHash(params)}:${statKey}`
}

function buildCacheKeyStatsImpact(companyId, params) {
	return `company:${companyId}:impact::params:${ObjectHash(params)}`
}

function buildCacheKeyStatsLeaderboard(companyId, params) {
	return `company:${companyId}:leaderboard::params:${ObjectHash(params)}`
}
function buildCacheKeyStatsSocioDemo(statKey, companyId) {
	return `company:${companyId}:socio-demo::${statKey}`
}

export const actions = {
	async retrieveGlobalIndicators({ dispatch }, { companyId, params, evolution }) {
		const statKey = evolution ? 'global_evolution' : 'global'
		return await dispatch('retrieveStatGlobal', {
			endpoint: `api/company/${companyId}/challenge-creators/stats/global/`,
			statKey,
			companyId,
			params,
		})
	},

	async retrieveMissionsByDate({ dispatch }, { companyId, params, evolution }) {
		const statKey = evolution ? 'missions_repartition_evolution' : 'missions_repartition'
		return await dispatch('retrieveStatGlobal', {
			endpoint: `api/company/${companyId}/challenge-creators/stats-by-date-type/`,
			statKey,
			companyId,
			params,
		})
	},

	async retrieveMissions({ dispatch }, { companyId, params }) {
		return await dispatch('retrieveStatGlobal', {
			endpoint: `api/company/${companyId}/challenges/?limit=6&has_participants=true&order_by=-participants&status[]=published`,
			statKey: 'missions',
			companyId,
			params,
		})
	},

	async retrieveMissionsNext({ state, commit }) {
		const nextEndpoint = state.statsGlobal.missions.next

		if (!nextEndpoint) return

		return await useNuxtApp()
			.$api(nextEndpoint)
			.then((data) => {
				commit('setStatsMissionsNext', { data })
				return data
			})
			.catch((err) => {
				commit('clearCacheGlobal', { statKey: 'missions' })
				throw err
			})
	},

	async retrieveStatGlobal({ state, commit }, { endpoint, statKey, companyId, params }) {
		if (isInCacheStatsGlobal(state, statKey, companyId, params)) {
			return state.statsGlobal[statKey]
		}
		return await useNuxtApp()
			.$api(endpoint, { params })
			.then((data) => {
				commit('setStatsGlobal', { statKey, data, companyId, params })
				return data
			})
			.catch((err) => {
				commit('clearCacheGlobal', { statKey })
				throw err
			})
	},
	async retrieveImpactIndicators({ state, commit }, { companyId, params }) {
		if (isInCacheStatsImpact(state, companyId, params)) {
			return state.statsImpact
		}
		return await useNuxtApp()
			.$api(`api/company/${companyId}/challenge-creators/stats/impact/`, {
				params,
			})
			.then((data) => {
				commit('setStatsImpact', { data, companyId, params })
				return data
			})
			.catch((err) => {
				commit('clearCacheImpact')
				throw err
			})
	},
	async retrieveAmbassadorLeaderboard({ state, commit }, { companyId, params }) {
		if (isInCacheStatsLeaderboard(state, companyId, params)) {
			return state.statsLeaderboard.results
		}
		return await useNuxtApp()
			.$api(`api/company/${companyId}/challenge-creators/stats/leaderboard/`, {
				params,
			})
			.then((data) => {
				commit('setStatsLeaderboard', { data, companyId, params })
				return data.results
			})
			.catch((err) => {
				commit('clearCacheLeaderboard')
				throw err
			})
	},

	async retrieveAmbassadorLeaderboardNext({ state, commit }) {
		const nextEndpoint = state.statsLeaderboard.next

		if (!nextEndpoint) return

		return await useNuxtApp()
			.$api(nextEndpoint)
			.then((data) => {
				commit('setStatsLeaderboardNext', { data })
				return data.results
			})
			.catch((err) => {
				commit('clearCacheLeaderboard')
				throw err
			})
	},

	async retrieveRepartitionByFollowers({ dispatch }, { companyId }) {
		return await dispatch('retrieveStatSocioDemo', {
			endpoint: `api/company/${companyId}/insta-accounts/stats/followers/`,
			statKey: 'instagram_followers',
			companyId,
		})
	},

	async retrieveRepartitionByFollowersTiktok({ dispatch }, { companyId }) {
		return await dispatch('retrieveStatSocioDemo', {
			endpoint: `api/company/${companyId}/tiktok-accounts/stats/followers/`,
			statKey: 'tiktok_followers',
			companyId,
		})
	},

	async retrieveRepartitionByAges({ dispatch }, { companyId }) {
		return await dispatch('retrieveStatSocioDemo', {
			endpoint: `api/company/${companyId}/creators/stats-age/`,
			statKey: 'repartition_by_ages',
			companyId,
		})
	},

	async retrieveStatSocioDemo({ state, commit }, { endpoint, statKey, companyId }) {
		if (isInCacheStatsSocioDemo(state, statKey, companyId)) {
			return state.statsSocioDemo[statKey]
		}
		return await useNuxtApp()
			.$api(endpoint)
			.then((data) => {
				commit('setStatsSocioDemo', { statKey, data, companyId })
				return data
			})
			.catch((err) => {
				commit('clearCacheSocioDemo', { statKey })
				throw err
			})
	},
}

export const mutations = {
	setStatsGlobal(state, { statKey, data, companyId, params }) {
		state.statsGlobal = { ...state.statsGlobal, [statKey]: data }

		state.ttl_statsGlobal[statKey] = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsGlobal(statKey, companyId, params)
		state.cache_key_statsGlobal[statKey] = cacheKey
	},

	setStatsMissionsNext(state, { data }) {
		const { missions } = state.statsGlobal
		missions.next = data.next
		missions.results = [...missions.results, ...data.results]
		state.ttl_statsGlobal.missions = Date.now() + state.cache_duration
	},

	setStatsImpact(state, { data, companyId, params }) {
		state.statsImpact = data
		state.ttl_statsImpact = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsImpact(companyId, params)
		state.cache_key_statsImpact = cacheKey
	},

	setStatsLeaderboard(state, { data, companyId, params }) {
		state.statsLeaderboard = data
		state.ttl_statsLeaderboard = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsLeaderboard(companyId, params)
		state.cache_key_statsLeaderboard = cacheKey
	},

	setStatsLeaderboardNext(state, { data }) {
		state.statsLeaderboard.next = data.next
		state.statsLeaderboard.results = [...state.statsLeaderboard.results, ...data.results]
		state.ttl_statsLeaderboard = Date.now() + state.cache_duration
	},

	setStatsSocioDemo(state, { statKey, data, companyId }) {
		state.statsSocioDemo = { ...state.statsSocioDemo, [statKey]: data }
		state.ttl_statsSocioDemo[statKey] = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsSocioDemo(statKey, companyId)
		state.cache_key_statsSocioDemo[statKey] = cacheKey
	},

	clearCacheGlobal(state, { statKey }) {
		state.statsGlobal[statKey] = null
		state.ttl_statsGlobal[statKey] = null
		state.cache_key_statsGlobal[statKey] = null
	},
	clearCacheImpact(state) {
		state.statsImpact = null
		state.ttl_statsImpact = null
		state.cache_key_statsImpact = null
	},
	clearCacheLeaderboard(state) {
		state.statsLeaderboard = { results: [], next: null, count: 0 }
		state.ttl_statsLeaderboard = null
		state.cache_key_statsLeaderboard = null
	},
	clearCacheSocioDemo(state, { statKey }) {
		state.statsSocioDemo[statKey] = null
		state.ttl_statsSocioDemo[statKey] = null
		state.cache_key_statsSocioDemo[statKey] = null
	},
}
